/*
 * Case builder.
 *
 * A design is two products, one per device brand. Each design control carries
 * both variant ids, so changing the phone in step 1 just reads the other
 * attribute — no refetch, no reload.
 *
 * Every id, price and path arrives from Liquid as a data attribute. There is
 * no currency formatting and no hardcoded route in here.
 *
 * The design controls are real radios named `id` inside a real form, so with
 * JavaScript off the chosen design still posts to /cart/add.
 */
(function () {
  'use strict';

  var instances = new WeakMap();

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function coarsePointer() {
    return window.matchMedia('(pointer: coarse)').matches;
  }

  function fill(template, values) {
    return Object.keys(values).reduce(function (text, key) {
      return text.split('[' + key + ']').join(values[key]);
    }, template || '');
  }

  /*
   * Roving tabindex over a group of role="radio" buttons: one stop in the tab
   * order, arrow keys to move between options, Home/End to jump.
   */
  function wireRadioGroup(group, onSelect, register) {
    if (!group) return;

    function options() {
      return Array.prototype.slice.call(group.querySelectorAll('[role="radio"]')).filter(function (option) {
        return !option.hasAttribute('hidden');
      });
    }

    function focusAt(index) {
      var list = options();
      if (!list.length) return;
      var next = list[(index + list.length) % list.length];
      next.focus();
      onSelect(next);
    }

    register(group, 'click', function (event) {
      var option = event.target.closest('[role="radio"]');
      if (!option || !group.contains(option)) return;
      if (option.getAttribute('aria-disabled') === 'true') return;
      onSelect(option);
    });

    register(group, 'keydown', function (event) {
      var list = options();
      var current = list.indexOf(document.activeElement);
      if (current === -1) return;

      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          event.preventDefault();
          focusAt(current + 1);
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault();
          focusAt(current - 1);
          break;
        case 'Home':
          event.preventDefault();
          focusAt(0);
          break;
        case 'End':
          event.preventDefault();
          focusAt(list.length - 1);
          break;
        default:
          break;
      }
    });
  }

  function create(root) {
    var deviceGroup = root.querySelector('[data-dcs-devices]');
    var tierGroup = root.querySelector('[data-dcs-tiers]');
    var designGroup = root.querySelector('[data-dcs-designs]');
    var designStep = root.querySelector('[data-dcs-design-step]');
    var explainer = root.querySelector('[data-dcs-explainer]');
    var priceEl = root.querySelector('[data-dcs-price]');
    var form = root.querySelector('[data-dcs-form]');
    var requestLink = root.querySelector('[data-dcs-request]');
    var errorEl = root.querySelector('[data-dcs-error]');
    var stage = root.querySelector('[data-dcs-stage]');
    var addButton = root.querySelector('[data-dcs-add]');

    if (!deviceGroup || !tierGroup || !form) return null;

    var cartAddUrl = root.getAttribute('data-cart-add-url');
    var cartUrl = root.getAttribute('data-cart-url');
    var soldOutLabel = root.getAttribute('data-sold-out-label') || '';
    var unavailableLabel = root.getAttribute('data-unavailable-label') || '';
    var errorText = root.getAttribute('data-error-text') || '';

    var listeners = [];
    function register(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      listeners.push([target, type, handler, options]);
    }

    var device = '';
    var tier = null;

    function setError(message) {
      if (errorEl) errorEl.textContent = message || '';
    }

    function check(group, selected) {
      if (!group) return;
      Array.prototype.forEach.call(group.querySelectorAll('[role="radio"]'), function (option) {
        var isOn = option === selected;
        option.setAttribute('aria-checked', isOn ? 'true' : 'false');
        option.setAttribute('tabindex', isOn ? '0' : '-1');
      });
    }

    function devices() {
      return Array.prototype.slice.call(deviceGroup.querySelectorAll('[data-dcs-device]'));
    }

    function tiers() {
      return Array.prototype.slice.call(tierGroup.querySelectorAll('[data-dcs-tier]'));
    }

    function checkedDesign() {
      return designGroup ? designGroup.querySelector('[data-dcs-design]:checked') : null;
    }

    function designVariant(design, key) {
      return design ? design.getAttribute('data-variant-' + key) || '' : '';
    }

    function designPrice(design, key) {
      return design ? design.getAttribute('data-price-' + key) || '' : '';
    }

    function designAvailable(design, key) {
      return design ? design.getAttribute('data-available-' + key) === 'true' : false;
    }

    /*
     * A design that exists for only one phone disables the other, and says so
     * on the button rather than failing silently at add-to-cart.
     */
    function refreshDevices() {
      var design = checkedDesign();

      devices().forEach(function (option) {
        var key = option.getAttribute('data-dcs-device') || '';
        var missing = design !== null && designVariant(design, key) === '';

        option.setAttribute('aria-disabled', missing ? 'true' : 'false');

        var note = option.querySelector('[data-dcs-device-note]');
        if (!note) return;
        note.textContent = missing
          ? fill(unavailableLabel, { device: option.getAttribute('data-device-title') || '' })
          : '';
        note.toggleAttribute('hidden', !missing);
      });
    }

    function availableDevice() {
      var design = checkedDesign();
      return (
        devices().filter(function (option) {
          return designVariant(design, option.getAttribute('data-dcs-device') || '') !== '';
        })[0] || null
      );
    }

    function refreshPrice() {
      var design = checkedDesign();
      var isAdd = tier && tier.getAttribute('data-behaviour') === 'add_to_cart';
      var value = isAdd ? designPrice(design, device) : (tier && tier.getAttribute('data-price-static')) || '';

      if (priceEl) priceEl.textContent = value;

      // Only the add-to-cart tier tracks the design; the others show a fixed price.
      tiers().forEach(function (option) {
        var slot = option.querySelector('[data-dcs-tier-price]');
        if (!slot) return;
        if (option.getAttribute('data-behaviour') === 'add_to_cart') slot.textContent = designPrice(design, device);
      });

      if (addButton && isAdd) {
        var soldOut = design !== null && designVariant(design, device) !== '' && !designAvailable(design, device);
        addButton.toggleAttribute('disabled', designVariant(design, device) === '' || soldOut);
      }

      var note = tier && tier.querySelector('[data-dcs-tier-note]');
      if (note && isAdd) {
        var showSoldOut = design !== null && designVariant(design, device) !== '' && !designAvailable(design, device);
        note.textContent = showSoldOut ? soldOutLabel : '';
        note.toggleAttribute('hidden', !showSoldOut);
      }
    }

    function syncVariantValues() {
      if (!designGroup) return;
      Array.prototype.forEach.call(designGroup.querySelectorAll('[data-dcs-design]'), function (design) {
        // Fall back to the other phone so a one-device design still has a value.
        design.value =
          designVariant(design, device) ||
          design.getAttribute('data-variant-iphone') ||
          design.getAttribute('data-variant-samsung') ||
          '';
      });
    }

    function requestHref(option) {
      var base = option.getAttribute('data-request-url');
      if (!base) return '';

      // Relative or absolute, both resolve against the current document.
      var url = new URL(base, window.location.origin);
      url.searchParams.set('device', device);
      url.searchParams.set('tier', option.getAttribute('data-dcs-tier') || '');
      return url.pathname + url.search + url.hash;
    }

    /*
     * The request link carries the device in its query string, so it has to be
     * rewritten when the phone changes and not only when the tier does.
     */
    function refreshRequest() {
      if (!requestLink || !tier) return;

      var isAdd = tier.getAttribute('data-behaviour') === 'add_to_cart';
      requestLink.toggleAttribute('hidden', isAdd);

      var href = requestHref(tier);
      if (href) {
        requestLink.setAttribute('href', href);
        requestLink.removeAttribute('role');
        requestLink.removeAttribute('aria-disabled');
      } else {
        requestLink.removeAttribute('href');
        requestLink.setAttribute('role', 'link');
        requestLink.setAttribute('aria-disabled', 'true');
      }
    }

    function applyTier(option) {
      if (!option) return;
      tier = option;
      check(tierGroup, option);
      setError('');

      var isAdd = option.getAttribute('data-behaviour') === 'add_to_cart';

      if (designStep) designStep.toggleAttribute('hidden', !isAdd);
      if (explainer) explainer.toggleAttribute('hidden', isAdd);
      if (addButton) addButton.toggleAttribute('hidden', !isAdd);

      refreshRequest();
      refreshPrice();
    }

    function applyDevice(option) {
      if (!option) return;
      device = option.getAttribute('data-dcs-device') || '';
      check(deviceGroup, option);

      Array.prototype.forEach.call(root.querySelectorAll('[data-dcs-frame]'), function (frame) {
        frame.toggleAttribute('hidden', frame.getAttribute('data-dcs-frame') !== device);
      });

      syncVariantValues();
      refreshRequest();
      refreshPrice();

      /*
       * Feature A's cards link per device too. Announcing the change lets them
       * follow along without either section knowing the other exists.
       */
      document.dispatchEvent(new CustomEvent('dcs:device', { detail: { device: device } }));
    }

    function applyDesign(design) {
      if (!design) return;
      design.checked = true;

      var image = design.getAttribute('data-design-image');
      Array.prototype.forEach.call(root.querySelectorAll('[data-dcs-design-layer]'), function (layer) {
        layer.style.backgroundImage = image ? 'url(' + image + ')' : '';
      });

      refreshDevices();

      // Never leave the customer on a phone this design was not made for.
      if (designVariant(design, device) === '') {
        var fallback = availableDevice();
        if (fallback) {
          applyDevice(fallback);
          return;
        }
      }

      syncVariantValues();
      refreshPrice();
    }

    wireRadioGroup(deviceGroup, applyDevice, register);
    wireRadioGroup(tierGroup, applyTier, register);

    // The design controls are native radios, so arrow keys are already handled.
    register(form, 'change', function (event) {
      if (event.target.hasAttribute('data-dcs-design')) applyDesign(event.target);
    });

    /* Cart ---------------------------------------------------------------- */

    function cartComponent() {
      // Detect rather than assume: this theme may run a drawer or a notification.
      return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    }

    function submit(event) {
      event.preventDefault();

      var design = checkedDesign();
      var variant = design ? design.value : '';
      if (!variant) {
        setError(errorText);
        return;
      }

      setError('');
      if (addButton) addButton.setAttribute('aria-busy', 'true');

      var cart = cartComponent();
      var payload = { items: [{ id: variant, quantity: 1 }] };

      if (cart && typeof cart.getSectionsToRender === 'function') {
        payload.sections = cart.getSectionsToRender().map(function (section) {
          return section.id;
        });
        payload.sections_url = window.location.pathname;
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
      }

      fetch(cartAddUrl + '.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      })
        .then(function (response) {
          return response.json();
        })
        .then(function (parsed) {
          if (parsed.status) {
            setError(parsed.description || parsed.message || errorText);
            return;
          }

          if (!cart || typeof cart.renderContents !== 'function') {
            window.location = cartUrl;
            return;
          }

          /*
           * The items payload has no top-level key, which is what the theme's
           * cart component looks up to render the line it just added.
           */
          if (!parsed.key && parsed.items && parsed.items.length) parsed.key = parsed.items[0].key;
          cart.renderContents(parsed);
        })
        .catch(function () {
          setError(errorText);
        })
        .finally(function () {
          if (addButton) addButton.removeAttribute('aria-busy');
        });
    }

    register(form, 'submit', submit);

    /* Pointer tilt --------------------------------------------------------- */

    if (stage && !reducedMotion() && !coarsePointer()) {
      /*
       * Passive listener and no preventDefault anywhere: on a hybrid device
       * the tilt can never swallow a scroll.
       */
      register(
        stage,
        'pointermove',
        function (event) {
          if (event.pointerType === 'touch') return;
          var rect = stage.getBoundingClientRect();
          if (!rect.width || !rect.height) return;
          stage.style.setProperty('--dcs-tilt-x', ((event.clientX - rect.left) / rect.width - 0.5).toFixed(3));
          stage.style.setProperty('--dcs-tilt-y', (0.5 - (event.clientY - rect.top) / rect.height).toFixed(3));
        },
        { passive: true }
      );

      register(stage, 'pointerleave', function () {
        stage.style.setProperty('--dcs-tilt-x', '0');
        stage.style.setProperty('--dcs-tilt-y', '0');
      });
    }

    /* Opening state -------------------------------------------------------- */

    var openingDevice = deviceGroup.querySelector('[aria-checked="true"]') || devices()[0] || null;
    // Seeded before the design is applied, because applyDesign reads it.
    device = openingDevice ? openingDevice.getAttribute('data-dcs-device') || '' : '';

    var openingDesign = checkedDesign() || (designGroup && designGroup.querySelector('[data-dcs-design]'));
    if (openingDesign) applyDesign(openingDesign);

    applyDevice(openingDevice);

    var openingTier = tierGroup.querySelector('[aria-checked="true"]');
    if (!openingTier || openingTier.hasAttribute('hidden')) {
      openingTier =
        tiers().filter(function (option) {
          return !option.hasAttribute('hidden');
        })[0] || null;
    }
    applyTier(openingTier);

    return {
      selectBlock: function (element) {
        if (element.hasAttribute('data-dcs-device')) applyDevice(element);
        if (element.hasAttribute('data-dcs-tier')) applyTier(element);
      },
      destroy: function () {
        listeners.forEach(function (entry) {
          entry[0].removeEventListener(entry[1], entry[2], entry[3]);
        });
        listeners.length = 0;
      },
    };
  }

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-dcs-case-builder]');

    Array.prototype.forEach.call(roots, function (root) {
      if (instances.has(root)) return;
      var instance = create(root);
      if (instance) instances.set(root, instance);
    });
  }

  function destroy(scope) {
    var roots = (scope || document).querySelectorAll('[data-dcs-case-builder]');

    Array.prototype.forEach.call(roots, function (root) {
      var instance = instances.get(root);
      if (!instance) return;
      instance.destroy();
      instances.delete(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroy(event.target);
  });

  document.addEventListener('shopify:block:select', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var root = target.closest('[data-dcs-case-builder]');
    var instance = root && instances.get(root);
    if (!instance) return;

    instance.selectBlock(target);
    target.scrollIntoView({ block: 'center', behavior: 'smooth' });
  });
})();
